import type { PeriodUnit, FreeUnit, RateLineFlags, RateLineScope } from '../../pages/Admin/Pricing/types'
import { RATE_LINE_SCOPE_OPTIONS } from '../../pages/Admin/Pricing/types'

// The 20261003 columns of a rate line, as table cells -- shared by the customer and the supplier
// rate-card editors so both sides describe a line the same way. Controlled: the parent owns the line.

const cellInputCls = 'border border-gray-200 dark:border-gray-600 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-60'

type Patch = Partial<RateLineFlags>

export function RateLineExtraHeaders() {
  return (
    <>
      <th className="px-2 py-2">Scope</th>
      <th className="px-2 py-2" title="Quantity included at no charge before the unit price applies (e.g. 7 days, 50 pallets, 5 HS items)">Free</th>
      <th className="px-2 py-2" title="Time basis of a quantity x time line, e.g. storage per wm per day / week">Per</th>
      <th className="px-2 py-2" title="At cost: no price, the amount comes from the real data. Optional: only if the customer asks. Enabled: switched off instead of deleted.">Flags</th>
    </>
  )
}

export function RateLineExtraCells({ line, onChange, disabled }: { line: RateLineFlags; onChange: (patch: Patch) => void; disabled?: boolean }) {
  const setFree = (qty: number | null, unit: FreeUnit | null) => onChange({ free_qty: qty, free_unit: qty == null ? null : (unit ?? 'each') })
  return (
    <>
      <td className="px-2 py-2">
        <select className={cellInputCls} disabled={disabled} value={line.scope}
          onChange={e => onChange({ scope: e.target.value as RateLineScope })}>
          {RATE_LINE_SCOPE_OPTIONS.map(o => <option key={o.value} value={o.value} title={o.hint}>{o.label}</option>)}
        </select>
      </td>
      <td className="px-2 py-2 whitespace-nowrap">
        <input type="number" step="any" min="0" disabled={disabled} className={cellInputCls + ' w-14'} placeholder="—"
          value={line.free_qty ?? ''} onChange={e => setFree(e.target.value === '' ? null : parseFloat(e.target.value), line.free_unit)} />
        <select className={cellInputCls + ' ml-1'} disabled={disabled || line.free_qty == null} value={line.free_unit ?? 'each'}
          onChange={e => onChange({ free_unit: e.target.value as FreeUnit })}>
          <option value="day">days</option><option value="week">weeks</option><option value="each">units</option>
        </select>
      </td>
      <td className="px-2 py-2">
        <select className={cellInputCls} disabled={disabled} value={line.period_unit ?? ''}
          onChange={e => onChange({ period_unit: (e.target.value || null) as PeriodUnit | null })}>
          <option value="">—</option><option value="day">day</option><option value="week">week</option>
        </select>
      </td>
      <td className="px-2 py-2 whitespace-nowrap text-xs">
        <label className="mr-2 cursor-pointer" title="Pass-through at cost: no unit price"><input type="checkbox" disabled={disabled} checked={line.is_at_cost} onChange={e => onChange({ is_at_cost: e.target.checked })} /> at cost</label>
        <label className="mr-2 cursor-pointer" title="Only charged when the customer asks"><input type="checkbox" disabled={disabled} checked={line.is_optional} onChange={e => onChange({ is_optional: e.target.checked })} /> optional</label>
        <label className="cursor-pointer" title="Untick to switch the line off without deleting it"><input type="checkbox" disabled={disabled} checked={line.is_enabled} onChange={e => onChange({ is_enabled: e.target.checked })} /> on</label>
      </td>
    </>
  )
}
